/**
 * The editable copy of a rule, with the chain expressed as explicit from/to pairs.
 *
 * The pairs are explicit precisely so that an overlapping pair *can* be entered and reported:
 * the invariant this editor exists to protect is that exactly one version governs any filing
 * date, and a form that made an overlap unexpressible would hide the error rather than surface
 * it. The validator that reports it is the server's.
 *
 * A draft holds triggers, shapes and period units as plain strings because the form's options
 * come from `GET /meta` at runtime. They are narrowed to the wire's vocabulary in `toRequest`,
 * which is the one place the two meet — and if a value the server has never heard of reaches it,
 * the server refuses the write with the sentence saying so.
 */

import type {
  CatalogueRule,
  CitationIn,
  RuleForFiling,
  StatusScope,
  UpsertRuleRequest,
  UpsertVersionRequest,
} from "~/api/schema";

export interface DraftPeriod {
  value: number;
  unit: string;
}

export interface DraftCitation {
  /** A local key, so a repeating block keeps its identity while it is being typed. */
  key: string;
  url: string | null;
  citationText: string | null;
  physicalLocation: string | null;
  role: NonNullable<CitationIn["role"]>;
  quote: string | null;
  language: string | null;
  ordinal: number | null;
}

export interface DraftVersion {
  key: string;
  id?: string;
  from: string;
  to: string | null;
  trigger: string;
  period: DraftPeriod | null;
  firstTerm: DraftPeriod | null;
  laterTerm: DraftPeriod | null;
  maxTerm: DraftPeriod | null;
  firstAnnuityYear: number | null;
  citations: DraftCitation[];
  statusScope: DraftStatusScope;
  /** True when `to` was derived by the server rather than typed by anyone. */
  inherited?: boolean;
}

/**
 * Which case statuses a version applies to. Three exclusive ways of saying it, held as one shape
 * so the form cannot express two at once — the server refuses that, and a control that can reach
 * a refused state is a control that wastes a round trip to say so.
 */
export interface DraftStatusScope {
  allStatuses: boolean;
  activity: "Live" | "Dead" | null;
  included: string[];
  excluded: string[];
}

export const everyStatus = (): DraftStatusScope => ({
  allStatuses: true,
  activity: null,
  included: [],
  excluded: [],
});

export interface RuleDraft {
  lane: string;
  previousLane: string | null;
  /** Null means every sub-matter — an absence, not an unknown. */
  subMatter: string | null;
  shape: string;
  adopted: string | null;
  repealed: string | null;
  versions: DraftVersion[];
}

let counter = 0;
const key = () => {
  counter += 1;
  return `draft-${counter}`;
};

const EMPTY_PARAMETERS = {
  period: null,
  firstTerm: null,
  laterTerm: null,
  maxTerm: null,
  firstAnnuityYear: null,
} as const;

/** Changing the shape resets every version's parameters to the defaults for that shape. */
export function parametersFor(
  shape: string,
): Pick<
  DraftVersion,
  "period" | "firstTerm" | "laterTerm" | "maxTerm" | "firstAnnuityYear"
> {
  if (shape === "term") {
    return {
      ...EMPTY_PARAMETERS,
      firstTerm: { value: 10, unit: "year" },
      laterTerm: { value: 10, unit: "year" },
    };
  }
  if (shape === "annuity") {
    return {
      ...EMPTY_PARAMETERS,
      maxTerm: { value: 20, unit: "year" },
      firstAnnuityYear: 3,
    };
  }
  return { ...EMPTY_PARAMETERS, period: { value: 6, unit: "month" } };
}

export function blankVersion(
  shape: string,
  from: string,
  trigger: string,
): DraftVersion {
  return {
    key: key(),
    from,
    to: null,
    trigger,
    citations: [],
    statusScope: everyStatus(),
    ...parametersFor(shape),
  };
}

export function draftFromRule(rule: RuleForFiling): RuleDraft {
  return {
    lane: rule.lane,
    previousLane: rule.lane,
    subMatter: rule.subMatter ?? null,
    shape: rule.shape,
    adopted: rule.adopted,
    repealed: rule.repealed,
    versions: rule.versions.map((version) => ({
      key: key(),
      id: version.id,
      from: version.from,
      to: version.to,
      inherited: version.inherited,
      trigger: version.trigger,
      period: version.period ?? null,
      firstTerm: version.firstTerm ?? null,
      laterTerm: version.laterTerm ?? null,
      maxTerm: version.maxTerm ?? null,
      firstAnnuityYear: version.firstAnnuityYear ?? null,
      statusScope: fromWireScope(version.statusScope),
      citations: (version.citations ?? []).map((citation) => ({
        key: key(),
        url: citation.url,
        citationText: citation.citationText ?? null,
        physicalLocation: citation.physicalLocation ?? null,
        role: citation.role,
        quote: citation.quote ?? null,
        language: citation.language ?? null,
        ordinal: citation.ordinal ?? null,
      })),
    })),
  };
}

/** A blank draft, seeded from the catalogue entry where the register is adopting a known rule. */
export function blankDraft(
  lane: string,
  catalogue: CatalogueRule | undefined,
  today: string,
): RuleDraft {
  const shape = catalogue?.shape ?? "deadline";
  const trigger = catalogue?.suggestedTriggers?.[0] ?? "filing";
  return {
    lane,
    previousLane: null,
    subMatter: null,
    shape,
    adopted: null,
    repealed: null,
    versions: [blankVersion(shape, today, trigger)],
  };
}

/** A new version starts where the last one ended — the only start that cannot overlap it. */
export function nextVersion(draft: RuleDraft, today: string): DraftVersion {
  const last = draft.versions[draft.versions.length - 1];
  return blankVersion(
    draft.shape,
    last?.to ?? today,
    last?.trigger ?? "filing",
  );
}

export function newCitation(): DraftCitation {
  return {
    key: key(),
    url: "",
    citationText: null,
    physicalLocation: null,
    role: "primary",
    quote: null,
    language: null,
    ordinal: null,
  };
}

type WirePeriod = NonNullable<UpsertVersionRequest["period"]>;
type WireTrigger = UpsertVersionRequest["trigger"];

const period = (value: DraftPeriod | null): WirePeriod | null =>
  value === null
    ? null
    : { value: value.value, unit: value.unit as WirePeriod["unit"] };

/** The wire body. Local keys and the inherited flag never leave the browser. */
function fromWireScope(scope: StatusScope | undefined): DraftStatusScope {
  if (!scope) return everyStatus();
  return {
    allStatuses: scope.allStatuses,
    activity: (scope.activity as "Live" | "Dead" | null) ?? null,
    included: [...scope.included],
    excluded: [...scope.excluded],
  };
}

export function toRequest(draft: RuleDraft): UpsertRuleRequest {
  return {
    shape: draft.shape as UpsertRuleRequest["shape"],
    subMatter: draft.subMatter,
    adopted: draft.adopted,
    repealed: draft.repealed,
    previousLane:
      draft.previousLane && draft.previousLane !== draft.lane
        ? draft.previousLane
        : null,
    versions: draft.versions.map((version) => ({
      id: version.id ?? null,
      from: version.from,
      to: version.to,
      trigger: version.trigger as WireTrigger,
      period: period(version.period),
      firstTerm: period(version.firstTerm),
      laterTerm: period(version.laterTerm),
      maxTerm: period(version.maxTerm),
      firstAnnuityYear: version.firstAnnuityYear,
      statusScope: version.statusScope,
      citations: version.citations
        .filter((citation) => citation.url || citation.citationText)
        .map(({ key: _key, ...citation }) => ({
          ...citation,
          modelClaimedStatus: null,
        })),
    })),
  };
}
